import React from 'react';

// This function is a custom hook that adds a keyboard event listener to the document
//   calls the specified function when a key is pressed.
const useGlobalKeyboardListener = (func: ({ key }: { key: string }) => void) => {
  React.useEffect(() => {
    document.addEventListener('keydown', func);
    return () => document.removeEventListener('keydown', func);
  }, [func]);
};

export type LatestOpenInteraction = '' | 'onFocus' | 'onMouseEnter';
export type UseHeadlessTooltipConfig = {
  hasFocus?: boolean;
  isHovering?: boolean;
  latestOpenInteraction?: LatestOpenInteraction;
  onOpen?: () => void;
  onClose?: () => void;
};

const useControlledState = <T>(controlledValue?: T, defaultVal?: T) => {
  const [internalValue, setInternalValue] = React.useState<T | null>(defaultVal ?? null);
  const val = React.useMemo(() => {
    if (controlledValue !== undefined) return controlledValue;
    return internalValue;
  }, [controlledValue, internalValue]);
  return [val, setInternalValue] as const;
};

/**
 * A custom hook that provides logic for a headless tooltip. This hook can be used in a controlled or uncontrolled way.
 * @param {UseHeadlessTooltipConfig} config - The configuration object for the hook.
 * @param {boolean} config.hasFocus - Whether or not the trigger element has focus. If this is not provided, the hook will track focus internally.
 * @param {boolean} config.isHovering - Whether or not the mouse is hovering over the trigger element. If this is not provided, the hook will track hover internally.
 * @param {LatestOpenInteraction} config.latestOpenInteraction - Whether or not the mouse is hovering over the trigger element. If this is not provided, the hook will track hover internally.
 * @param {Function} config.onOpen - A callback that will be invoked when the tooltip is opened.
 * @param {Function} config.onClose - A callback that will be invoked when the tooltip is closed.
 * @returns {object} - An object containing the following properties:
 *
 * {boolean} hasFocus - Whether or not the trigger element has focus.
 *
 * {boolean} isHovering - Whether or not the mouse is hovering over the trigger element.
 *
 * {'' | 'onFocus' | 'onMouseEnter'} latestOpenInteraction - which was the last operation that is used to control if the tooltip should show or not.
 *
 * {string} finalLatestOpenInteraction - The most recent interaction that caused the tooltip to open.
 *
 * {HTMLDivElement} referenceElement - The trigger element.
 *
 * {function} setReferenceElement - A function that sets the trigger element.
 *
 * {boolean} shouldShowPopover - Whether or not the tooltip is visible.
 *
 * {function} showTooltip - A function that opens the tooltip.
 *
 * {function} hideTooltip - A function that closes the tooltip.
 *
 * {function} onFocus - A function that should be invoked when the trigger element receives focus.
 *
 * {function} onBlur - A function that should be invoked when the trigger element loses focus.
 *
 * {function} onMouseEnter - A function that should be invoked when the mouse enters the trigger element.
 *
 * {function} onMouseLeave - A function that should be invoked when the mouse leaves the trigger element.
 * @example
 * const tooltipHelpers = useHeadlessTooltip({
 *    hasFocus: true,
 *    isHovering: false,
 *    onOpen: () => console.log('tooltip opened'),
 *    onClose: () => console.log('tooltip closed'),
 * });
 * ...
 *   <div>
 *     <button
 *        ref={tooltipHelpers.setReferenceElement}
 *        onMouseEnter={tooltipHelpers.onMouseEnter}
 *        onMouseLeave={tooltipHelpers.onMouseLeave}
 *        onPointerEnter={tooltipHelpers.onMouseEnter}
 *        onPointerLeave={tooltipHelpers.onMouseLeave}
 *        onFocus={tooltipHelpers.onFocus}
 *        onBlur={tooltipHelpers.onBlur}
 *     >hello</button>
 *     <DSPopperJS
 *       referenceElement={tooltipHelpers.referenceElement}
 *       shouldShowPopover={tooltipHelpers.shouldShowPopover}
 *     >
 *        <div>Tooltip content</div>
 *     </DSPopperJS>
 *   </div>
 */
export const useHeadlessTooltip = (config?: UseHeadlessTooltipConfig) => {
  // Extract the configuration values or set default values if they are not provided.
  const { hasFocus, isHovering, latestOpenInteraction, onOpen = () => {}, onClose = () => {} } = config ?? {};

  // this pattern is basically a way to allow usage of the hook in a controlled or uncontrolled way.
  const [finalHasFocus, setInternalHasFocus] = useControlledState<boolean>(hasFocus, false);
  const [finalIsHovering, setInternalIsHovering] = useControlledState<boolean>(isHovering, false);
  const [finalLatestOpenInteraction, setLatestOpenInteraction] = useControlledState<string>(latestOpenInteraction, '');

  // end of controlled/uncontrolled pattern

  // Set up state variables for the tooltip's visibility and the most recent interaction that caused it to open.
  //  The most recent interaction is used to determine whether or not to close the tooltip when the user
  //  moves the mouse away from the trigger element.
  const [referenceElement, setReferenceElement] = React.useState<HTMLDivElement | null>(null);
  const [shouldShowPopover, setShouldShowPopover] = React.useState<boolean>(false);

  // memoized versions of the show and hide functions
  //  those include the callbacks to allow "controlled" usage of the hook
  const showTooltip = React.useCallback(() => {
    setShouldShowPopover(true);
    onOpen();
  }, [onOpen]);

  const hideTooltip = React.useCallback(() => {
    setShouldShowPopover(false);
    onClose();
  }, [onClose]);

  const trackLatestKeyboardInteraction = React.useCallback(() => {
    if (!shouldShowPopover) {
      setLatestOpenInteraction('onFocus');
    }
  }, [setLatestOpenInteraction, shouldShowPopover]);

  const trackLatestMouseInteraction = React.useCallback(() => {
    if (!shouldShowPopover) {
      setLatestOpenInteraction('onMouseEnter');
    }
  }, [setLatestOpenInteraction, shouldShowPopover]);

  // following here are the event handlers that are used to control the tooltip's visibility logic
  const onFocus = React.useCallback(() => {
    setInternalHasFocus(true); // uncontrolled usage tracking
    trackLatestKeyboardInteraction();
    if (!shouldShowPopover) {
      showTooltip();
    }
  }, [setInternalHasFocus, shouldShowPopover, showTooltip, trackLatestKeyboardInteraction]);

  const onBlur = React.useCallback(() => {
    setInternalHasFocus(false); // uncontrolled usage tracking
    // blur may triggered via keyboard navigation or mouse interaction
    // every time the mouse is hovering, we want to keep the tooltip open
    // but if the mouse is not hovering, we want to close the tooltip only if the last interaction was via keyboard
    // e.g if the user keyboard navigates to the trigger element and then moves the mouse away, we want to keep the tooltip open
    if (!finalIsHovering || finalLatestOpenInteraction === 'onFocus') hideTooltip();
  }, [setInternalHasFocus, finalIsHovering, finalLatestOpenInteraction, hideTooltip]);

  const onMouseEnter = React.useCallback(() => {
    setInternalIsHovering(true); // uncontrolled usage tracking
    // kind of debouncing the mouse enter event to reduce the avarge O() notation of the function
    // even if this is continually invoked, it will be O(1) and controlled callbacks will be invoked only once
    trackLatestMouseInteraction();
    if (!shouldShowPopover) {
      showTooltip();
    }
  }, [setInternalIsHovering, shouldShowPopover, showTooltip, trackLatestMouseInteraction]);

  const onMouseLeave = React.useCallback(() => {
    setInternalIsHovering(false); // uncontrolled usage tracking
    // mouse leave may be triggered via keyboard navigation or mouse interaction
    // every time the trigger element has focus, we want to keep the tooltip open
    // but if the trigger element does not have focus, we want to close the tooltip only if the last interaction was via mouse
    // e.g if the user keyboard navigates to the trigger element and then moves the mouse away, we want to keep the tooltip open
    if (!finalHasFocus || finalLatestOpenInteraction === 'onMouseEnter') hideTooltip();
  }, [setInternalIsHovering, finalHasFocus, finalLatestOpenInteraction, hideTooltip]);

  const handleEscKey = React.useCallback(
    ({ key }: { key: string }) => {
      // accessibility standard: pressing escape should close the tooltip
      if (key === 'Escape') hideTooltip();
    },
    [hideTooltip],
  );

  // accessibilty standard: pressing escape should close the tooltip no matter where the focus is
  useGlobalKeyboardListener(handleEscKey);

  return React.useMemo(
    () => ({
      hasFocus: finalHasFocus,
      isHovering: finalIsHovering,
      latestOpenInteraction: finalLatestOpenInteraction,
      referenceElement,
      setReferenceElement,
      shouldShowPopover,
      showTooltip,
      hideTooltip,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
    }),
    [
      finalHasFocus,
      finalIsHovering,
      finalLatestOpenInteraction,
      referenceElement,
      shouldShowPopover,
      showTooltip,
      hideTooltip,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
    ],
  );
};
